import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import type { SxProps } from "@mui/material";
import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useRef, useState } from "react";
import type { DownloadOption } from "./MultiOptionDownloadButton";

interface Props {
  readonly loading: boolean;
  readonly primaryOption: DownloadOption;
  readonly secondaryOptions: DownloadOption[];
  readonly icon: ReactNode;
  readonly variant?: "outlined" | "contained" | "text";
  readonly color?: "primary" | "secondary" | "error" | "inherit";
  readonly sx?: SxProps;
  readonly onDownload: (key: string) => Promise<void>;
  readonly buttonText: string;
}

const PrimaryMenuDownloadButton: FC<Props> = ({
  loading,
  primaryOption,
  secondaryOptions,
  icon,
  variant = "outlined",
  color = "secondary",
  sx,
  onDownload,
  buttonText,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  return (
    <>
      <ButtonGroup
        variant={variant}
        color={color}
        ref={anchorRef}
        sx={sx}
        aria-label={buttonText}
        disabled={loading || primaryOption.disabled}
      >
        <Button
          startIcon={icon}
          onClick={() => onDownload(primaryOption.name)}
          loading={loading}
          loadingPosition="start"
        >
          {buttonText}
        </Button>
        <Button
          size="small"
          aria-label="Więcej opcji pobierania"
          aria-controls={open ? "split-download-menu" : undefined}
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={() => setOpen((o) => !o)}
          sx={{ px: 0.5, minWidth: 44 }}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        open={open && secondaryOptions.length > 0}
        anchorEl={anchorRef.current}
        transition
        placement="bottom-end"
        style={{ zIndex: 1 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <MenuList id="secondary-options-menu" autoFocusItem>
                  {secondaryOptions.map((o) => (
                    <MenuItem
                      key={o.name}
                      disabled={o.disabled}
                      onClick={async () => {
                        setOpen(false);
                        await onDownload(o.name);
                      }}
                    >
                      {o.name}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default PrimaryMenuDownloadButton;
